/**
 * Shared look for Admin → Ask ABHI: site tokens (the admin gold / ivory
 * theme used by AuditLogPage and UsersPage), card + heading styles, and
 * small formatting helpers.
 */

export const INK = '#0f0a07';
export const MUTED = 'rgba(15, 10, 7, 0.6)';
export const SUBTLE = 'rgba(15, 10, 7, 0.42)';
export const LINE = 'rgba(15, 10, 7, 0.08)';
export const SURFACE = 'rgba(255, 250, 235, 0.6)';
export const GOLD = '#bb8c3f';
export const GOLD_LIGHT = '#e9c46a';
export const GOLD_DARK = '#7a5a22';
export const GOLD_TINT = 'rgba(187, 140, 63, 0.14)';
export const IVORY_LIGHT = '#fdf9f0';
export const GOLD_BUTTON = `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`;
export const FONT = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

/** The admin "premium card": ivory, gold-tinted border, gold hairline on top. */
export const card = {
  position: 'relative',
  bgcolor: IVORY_LIGHT,
  border: '1px solid rgba(187, 140, 63, 0.18)',
  borderRadius: '12px',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
  },
} as const;

/** Gold uppercase eyebrow above a card title. */
export const eyebrow = {
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: GOLD,
} as const;

export const sectionTitle = {
  fontFamily: FONT,
  fontSize: 16,
  fontWeight: 650,
  letterSpacing: '-0.01em',
  color: INK,
} as const;

/** Answer status → label and colours (status, so never the series gold). */
export const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  answered: { label: 'Answered', color: '#047857', bg: '#ecfdf5' },
  clarify: { label: 'Asked to Clarify', color: '#1d4ed8', bg: '#eff6ff' },
  refused: { label: 'Out of Scope', color: '#52525b', bg: '#f4f4f5' },
  unverified: { label: 'Held Back', color: '#b45309', bg: '#fffbeb' },
  error: { label: 'Error', color: '#b91c1c', bg: '#fef2f2' },
};

export function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "just now", "5m ago", "3h ago", "Yesterday", "Oct 28". */
export function ago(iso: string): string {
  const d = new Date(iso);
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 172800) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')
  ).toUpperCase();
}
